//src/web/pages/Presentation/HlsPlayer.tsx

import React, { useEffect, useRef, useState } from 'react';
import Hls from "hls.js";

export const HLSPlayer = ({
  src,
  poster,
  muted,
  videoId,
  style = {},
  onOrientation,
  isLandscape,
  onReady,
}: {
  src: string;
  poster?: string;
  muted: boolean;
  videoId: string;
  style?: React.CSSProperties;
  onOrientation?: (isLandscape: boolean) => void;
  isLandscape?: boolean | null;
  onReady?: () => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
const pendingPlayRef = useRef(false);
const hlsRef = useRef<Hls | null>(null);
  const [showControls, setShowControls] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [userPaused, setUserPaused] = useState(false);

  const emitOrientation = (v: HTMLVideoElement) => {
    if (!v.videoWidth || !v.videoHeight) return;
    onOrientation?.(v.videoWidth > v.videoHeight);
  };

  //Play/pause
  const PlayIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <path d="M7 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="#bcb4b4"
  >
    <rect x="7.2" y="5" width="3.6" height="14" rx="1.2" />
    <rect x="13.2" y="5" width="3.6" height="14" rx="1.2" />
  </svg>
);

  //Loudspeakers
  const SpeakerOnIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15 9a4 4 0 0 1 0 6" />
    <path d="M18 7a7 7 0 0 1 0 10" />
  </svg>
);


const SpeakerOffIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#bbb"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

  /* ---------------- HLS attach ---------------- */
useEffect(() => {
  const video = videoRef.current;
  if (!video || !src) return;

  let cancelled = false;
  let hls: Hls | null = null;
  hlsRef.current = null;

  const tryPendingPlay = () => {
    if (cancelled) return;
    if (!videoRef.current) return;
    if (!pendingPlayRef.current) return;
    if (userPaused) return;

    const v = videoRef.current;

    if (v.readyState >= 2) {
      v.play().catch(() => {});
    }
  };

  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = src;
    video.load();

    const handleCanPlay = () => {
      tryPendingPlay();
    };

    video.addEventListener("canplay", handleCanPlay);

    return () => {
      cancelled = true;
      video.removeEventListener("canplay", handleCanPlay);
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }

  if (Hls.isSupported()) {
    hls = new Hls({ startLevel: 0, enableWorker: true });
    hlsRef.current = hls;

    hls.attachMedia(video);

    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      if (cancelled) return;
      hls?.loadSource(src);
    });

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      tryPendingPlay();
    });

    const handleCanPlay = () => {
      tryPendingPlay();
    };

    video.addEventListener("canplay", handleCanPlay);

    return () => {
      cancelled = true;
      video.removeEventListener("canplay", handleCanPlay);

      if (hls) {
        hls.destroy();
      }

      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }

  return () => {
    cancelled = true;
    video.pause();
    video.removeAttribute("src");
    video.load();
  };
}, [src]);

  /* ---------------- autoplay logic ---------------- */

//listener
useEffect(() => {
  const handleOtherVideoPlay = (event: Event) => {
    const customEvent = event as CustomEvent<{ videoId: string }>;
    const playingId = customEvent.detail?.videoId;

    if (playingId === videoId) return;

    const v = videoRef.current;
    if (!v) return;

    pendingPlayRef.current = false;

    if (!v.paused) {
      v.pause();
    }
  };

  window.addEventListener("preview-video-play", handleOtherVideoPlay as EventListener);

  return () => {
    window.removeEventListener("preview-video-play", handleOtherVideoPlay as EventListener);
  };
}, [videoId]);
  /* ---------------- progress tracking ---------------- */
  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

const handleScrub = (value: number) => {
  const v = videoRef.current;
  if (!v || !v.duration) return;

  v.currentTime = (v.duration * value) / 100;
  setProgress(value);
};

  /* ---------------- time ---------------- */
  const formatTime = (sec: number) => {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};


  /* ---------------- volume ---------------- */
  const handleVolume = (value: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = value;
    setVolume(value);
  };

  useEffect(() => {
  const v = videoRef.current;
  if (!v) return;
  v.muted = isMuted;
}, [isMuted]);

  /* ---------------- play/pause ---------------- */
const togglePlay = () => {
  const v = videoRef.current;
  if (!v) return;

  if (!v.paused && !v.ended) {
    pendingPlayRef.current = false;
    v.pause();
    setUserPaused(true);
    return;
  }

  setUserPaused(false);
  pendingPlayRef.current = true;

  window.dispatchEvent(
    new CustomEvent("preview-video-play", {
      detail: { videoId },
    })
  );

  if (v.readyState >= 2) {
    v.play().catch(() => {});
  }
};

  /* ---------------- render ---------------- */
 return (
  <div
    style={{
      position: "relative",
      width: "100%",
      height: "100%",
      overflow: "hidden",
    }}
    onMouseEnter={() => setShowControls(true)}
    onMouseLeave={() => setShowControls(false)}
  >
    {/* VIDEO */}
    <video
      ref={videoRef}
      poster={poster}
      muted={isMuted}
      playsInline
      preload="metadata"
      controls={false}
      onPointerDown={togglePlay}
      onLoadedMetadata={(e) => {
        emitOrientation(e.currentTarget);
      }}
 onLoadedData={(e) => {
  emitOrientation(e.currentTarget);
  onReady?.();
}}

onCanPlay={(e) => {
  emitOrientation(e.currentTarget);

  const v = videoRef.current;
  if (!v) return;
  if (!pendingPlayRef.current) return;
  if (userPaused) return;

  v.play().catch(() => {});
}}

 onTimeUpdate={onTimeUpdate}

onPlay={() => {
  setIsPlaying(true);
  setUserPaused(false);
  pendingPlayRef.current = false;
}}

onPause={() => {
  setIsPlaying(false);
}}

onEnded={() => {
  const v = videoRef.current;
  if (!v) return;

  pendingPlayRef.current = false;
  v.currentTime = 0;
  setIsPlaying(false);
  setUserPaused(true);
}}

      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
        display: "block",
        transform: "translateZ(0)",
        backfaceVisibility: "hidden",
        ...style,
      }}
    />

    {/* CONTROLS */}
    <div
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        width: "100%",
        background: "rgba(0,0,0,0.65)",
        backdropFilter: "blur(6px)",
        opacity: showControls ? 1 : 0,
        transition: "opacity 200ms ease",
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        overflow: "hidden",
      }}
      onClick={(e) => e.stopPropagation()} 
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: isLandscape ? "7px 14px" : "5px 8px",
        }}
      >
        {/* LEFT — PLAY */}
        <div style={{ display: "flex", alignItems: "center" }}>
    <button
  onPointerDown={(e) => {
    e.stopPropagation();
    togglePlay();
  }}
  style={controlBtn}
>
  {isPlaying ? <PauseIcon /> : <PlayIcon />}

</button>
</div>

        {/* CENTER — MAIN SCRUBBER */}
<div
  style={{
    flex: "1 1 auto",
    display: "flex",
    alignItems: "center",
    padding: "0 6px",
    maxWidth: isLandscape ? "60%" : "40%",   // 🔥 limits how wide the scrubber can ever become
    minWidth: 120,     // keeps it usable on small videos
  }}
>
  
<input
  type="range"
  min={0}
  max={100}
  value={progress}
  onPointerDown={(e) => e.stopPropagation()}
  onChange={(e) => handleScrub(Number(e.target.value))}
  className="custom-scrubber"
  style={{
    width: "100%",
    height: 3,
    background: `linear-gradient(
      to right,
      #d8cece 0%,
      #d8cece ${progress}%,
      #555 ${progress}%,
      #555 100%
    )`,
  }}
/>
</div>


        {/* RIGHT — TIME + VOLUME */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
          }}
        >
{/* TIME */}
<span
  style={{
    fontSize: 11,
    color: "#bbb",
    whiteSpace: "nowrap",
    marginLeft: -2,
    marginRight: 7,
    fontVariantNumeric: "tabular-nums", // 👈 stable width numbers
  }}
>
  {formatTime(videoRef.current?.currentTime || 0)}/
  {formatTime(videoRef.current?.duration || 0)}
</span>


          {/* SPEAKER */}
          <button
  onClick={(e) => {
    e.stopPropagation();
    setIsMuted(!isMuted);
  }}
  style={controlBtnVolume}
>
            {isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
          </button>

          {/* VOLUME */}
          <input
  type="range"
  min={0}
  max={1}
  step={0.05}
  value={isMuted ? 0 : volume}
  onPointerDown={(e) => e.stopPropagation()}
  onChange={(e) => {
    const val = Number(e.target.value);
    setIsMuted(val === 0);
    handleVolume(val);
  }}
  className="volume-slider"
  style={{
    width: isLandscape ? 85 : 65,
    height: 2,
    background: `linear-gradient(
      to right,
      #bcb3b3 0%,
      #d8cece ${(isMuted ? 0 : volume) * 100}%,
      #666 ${(isMuted ? 0 : volume) * 100}%,
      #666 100%
    )`,
  }}
/>
        </div>
      </div>
    </div>
  </div>
);

};

const controlBtn: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24,       // 👈 locks height
  lineHeight: 1,    // 👈 prevents font expansion
  padding: 0,       // 👈 removes vertical shift
};

const controlBtnVolume: React.CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#bcb4b4",
  borderRadius: 6,
  cursor: "pointer",
  fontSize: 20,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  height: 24,
  lineHeight: 1,
  padding: 0,
};
